import React from 'react';
import { FeatherX, FeatherFileText, FeatherAlertTriangle, FeatherCheckSquare, FeatherUsers, FeatherClipboard, FeatherTag, FeatherMessageSquare } from '../ui/icons';
import './NotesPreview.css';

import { FeatherTarget } from '../ui/icons';

const NOTE_TYPES = {
    overallAssessment: {
        label: 'Overall Assessment',
        icon: <FeatherFileText size={20} />,
        color: '#3b82f6'
    },
    issues: {
        label: 'Issues/Concerns',
        icon: <FeatherAlertTriangle size={20} />,
        color: '#ef4444'
    },
    actionItems: {
        label: 'Action Items',
        icon: <FeatherCheckSquare size={20} />,
        color: '#10b981'
    },
    resourceObservations: {
        label: 'Resource Observations',
        icon: <FeatherUsers size={20} />,
        color: '#8b5cf6'
    },
    taskConcerns: {
        label: 'Task Concerns',
        icon: <FeatherClipboard size={20} />,
        color: '#f59e0b'
    },
    featureTagNotes: {
        label: 'Feature Tag Notes',
        icon: <FeatherTag size={20} />,
        color: '#ec4899'
    },
    additionalComments: {
        label: 'Additional Comments',
        icon: <FeatherMessageSquare size={20} />,
        color: '#06b6d4'
    },
    goals: {
        label: 'Manager Goals',
        icon: <FeatherTarget size={20} />,
        color: '#3b82f6'
    }
};

const NotesPreview = ({ isOpen, onClose, notes }) => {
    if (!isOpen) return null;

    const hasNote = (noteType) => {
        if (noteType === 'goals') {
            const goals = notes?.goals || {};
            // Goals are valid if there are goals set OR if "no goals" is explicitly marked
            return goals.noGoals === true || (goals.tasks?.length > 0) || (goals.features?.length > 0) || (goals.personal?.length > 0);
        }
        return notes && notes[noteType] && notes[noteType].trim() !== '';
    };

    const getNoteContent = (noteType) => {
        if (noteType === 'goals') {
            const goals = notes?.goals || {};
            
            // Handle "no goals" case
            if (goals.noGoals === true) {
                return 'No goals for today';
            }
            
            let content = '';
            if (goals.tasks && goals.tasks.length > 0) {
                content += 'Task Goals:\n';
                goals.tasks.forEach((goal, idx) => {
                    content += `${idx + 1}. [${goal.displayId}] ${goal.taskTitle} - ${goal.status === 'achieved' ? '✓ Achieved' : goal.status === 'not-achieved' ? '✗ Not Achieved' : '⏱ Pending'}\n`;
                });
                content += '\n';
            }
            if (goals.features && goals.features.length > 0) {
                content += 'Feature Goals:\n';
                goals.features.forEach((goal, idx) => {
                    content += `${idx + 1}. ${goal.featureName} - ${goal.status === 'achieved' ? '✓ Achieved' : goal.status === 'not-achieved' ? '✗ Not Achieved' : '⏱ Pending'}\n`;
                });
                content += '\n';
            }
            if (goals.personal && goals.personal.length > 0) {
                content += 'Personal Goals:\n';
                goals.personal.forEach((goal, idx) => {
                    content += `${idx + 1}. ${goal.description} - ${goal.status === 'achieved' ? '✓ Achieved' : goal.status === 'not-achieved' ? '✗ Not Achieved' : '⏱ Pending'}\n`;
                });
            }
            return content.trim() || null;
        }
        const content = notes?.[noteType] || '';
        if (!content.trim()) return null;
        return content;
    };

    const notesWithContent = Object.keys(NOTE_TYPES).filter(key => hasNote(key));
    const totalNotes = notesWithContent.length;

    return (
        <div className="notes-preview-overlay" onClick={onClose}>
            <div className="notes-preview-modal" onClick={(e) => e.stopPropagation()}>
                <div className="notes-preview-header">
                    <h2 className="notes-preview-title">Review Notes Preview</h2>
                    <button 
                        className="notes-preview-close"
                        onClick={onClose}
                        aria-label="Close preview"
                    >
                        <FeatherX size={20} />
                    </button>
                </div>

                <div className="notes-preview-content">
                    {totalNotes === 0 ? (
                        <div className="notes-preview-empty">
                            <p>No notes entered yet</p>
                        </div>
                    ) : (
                        <>
                            {/* Wave Layout */}
                            <div className="notes-preview-wave">
                                <div className="notes-preview-wave-container">
                                    {Object.entries(NOTE_TYPES).map(([noteType, config], index) => {
                                        const hasValue = hasNote(noteType);
                                        if (!hasValue) return null;

                                        // Create wave pattern with alternating heights
                                        const waveOffset = Math.sin((index / notesWithContent.length) * Math.PI * 2) * 30;
                                        const delay = index * 0.1;

                                        return (
                                            <div
                                                key={noteType}
                                                className="notes-preview-wave-item"
                                                style={{
                                                    '--wave-offset': `${waveOffset}px`,
                                                    '--delay': `${delay}s`,
                                                    '--color': config.color
                                                }}
                                            >
                                                <div className="notes-preview-wave-icon" style={{ color: config.color, backgroundColor: `${config.color}15` }}>
                                                    {config.icon}
                                                </div>
                                                <div className="notes-preview-wave-label">{config.label}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="notes-preview-wave-summary">
                                    <div className="notes-preview-wave-summary-icon">
                                        <FeatherFileText size={24} />
                                    </div>
                                    <div className="notes-preview-wave-summary-text">
                                        <div className="notes-preview-wave-summary-count">{totalNotes}</div>
                                        <div className="notes-preview-wave-summary-label">Total Notes</div>
                                    </div>
                                </div>
                            </div>

                            {/* Detailed Preview Cards */}
                            <div className="notes-preview-details">
                                {Object.entries(NOTE_TYPES).map(([noteType, config]) => {
                                    const content = getNoteContent(noteType);
                                    if (!content) return null;

                                    return (
                                        <div key={noteType} className="notes-preview-card">
                                            <div className="notes-preview-card-header" style={{ borderLeftColor: config.color }}>
                                                <div className="notes-preview-card-icon" style={{ color: config.color }}>
                                                    {config.icon}
                                                </div>
                                                <h3 className="notes-preview-card-title">{config.label}</h3>
                                            </div>
                                            <div className="notes-preview-card-content">
                                                <p className="notes-preview-card-text">{content}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotesPreview;

